import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import {
  Flight,
  FlightCore,
  FlightSearch,
  SkyscannerResults,
  FlightFilter,
  FlightsWidgets,
  SkyscannerResultsV3,
  Suggest,
} from 'types';
import { FlightService } from 'api';
import { Subscription, throwError } from 'rxjs';
import { format } from 'date-fns';
import { mapError } from '../../../utils';
import { parsePrice } from '../../../mapper/flights/searchMapperV3';
import i18n from '../../../plugins/i18n';
import { map, finalize, timeoutWith } from 'rxjs/operators';
import { Events } from '../../../analytics';

let pollingSubscription: Subscription = null;
let nearbyDatesSubscription: Subscription = null;
const PAGE_LIMIT = 30;

export class State {
  searchCriteria: Flight.SearchCriteria = Flight.SearchCriteria.empty();
  skyscannerRedirectLink: string = '';
  filterIsOpened: boolean = false;
  filter: FlightFilter.Filter = null;
  results: FlightSearch.Itineraries[] = [];
  searchState: FlightSearch.SearchState = FlightSearch.SearchState.default();
  nearbyDatesWidget: FlightsWidgets.NearbyDates = null;
  page: number = 1;
  destinations: FlightSearch.Destinations = undefined;
  destinationsLoading: boolean = true;
}

const getters = <GetterTree<State, any>>{
  isOneWay(state: State): boolean {
    return !!state.searchCriteria.returnDate;
  },
  getOffers(state: State): FlightSearch.Itineraries[] {
    if (!state.filter) {
      return state.results.sort((a, b) => {
        return sortByDuration(a, b);
      });
    }

    const priceCondition = (offer: FlightSearch.Itineraries): boolean => {
      return (
        offer.prices[0].totalPrice >= state.filter.priceRange.left &&
        offer.prices[0].totalPrice <= state.filter.priceRange.right
      );
    };

    const stopCondition = (offer: FlightSearch.Itineraries): boolean => {
      const stop = state.filter.stops.filter(it => it.selected)[0];
      if (!stop) {
        return true;
      }
      if (stop.count === -1) {
        return true;
      }

      return (
        offer.outboundLeg.stations.stops.length === stop.count ||
        (offer.inboundLeg &&
          offer.inboundLeg.stations.stops.length === stop.count)
      );
    };

    const carrierCondition = (offer: FlightSearch.Itineraries): boolean => {
      return (
        state.filter.carriers.nothingSelected() ||
        state.filter.carriers.filterSelected().every(it => {
          const id = (it as FlightFilter.Carrier).id;
          return [offer.outboundLeg, offer.inboundLeg]
            .filter(x => x)
            .every(x => x.carriers.findIndex(a => a.id === id) >= 0);
        })
      );
    };

    const airportCondition = (offer: FlightSearch.Itineraries): boolean => {
      return (
        state.filter.airports.nothingSelected() ||
        [
          ...state.filter.airports
            .filterSelected()
            .groupBy(it => (it as FlightFilter.Airport).place.parent.id),
        ]
          .map(x =>
            (x[1] as FlightFilter.Airport[]).some(it => {
              const legs = [offer.outboundLeg, offer.inboundLeg].filter(x => x);
              const cond = (l: FlightSearch.Leg): boolean => {
                return [l.stations.destination, l.stations.origin].some(
                  z => z.id === it.place.id,
                );
              };
              if (x[1].length > 1) {
                return legs.some(it => cond(it));
              } else {
                return legs.every(it => cond(it));
              }
            }),
          )
          .every(it => it)
      );
    };

    const filtered = state.results.filter(it => {
      return (
        priceCondition(it) &&
        stopCondition(it) &&
        airportCondition(it) &&
        carrierCondition(it)
      );
    });

    const getMinDuration = (x: FlightSearch.Itineraries): number => {
      return [x.outboundLeg, x.inboundLeg]
        .filter(it => it)
        .map(it => it.duration.duration)
        .min();
    };

    const sortByDuration = (
      a: FlightSearch.Itineraries,
      b: FlightSearch.Itineraries,
    ) => {
      const stopsCount = (x: FlightSearch.Itineraries): number => {
        return (
          x.outboundLeg.stations.stops.length +
          (x.inboundLeg ? x.inboundLeg.stations?.stops.length : 0)
        );
      };

      const aStops = stopsCount(a);
      const bStops = stopsCount(b);
      const durationDiff = getMinDuration(a) - getMinDuration(b);
      const priceDiff = a.prices[0].totalPrice - b.prices[0].totalPrice;

      if (aStops !== bStops) {
        return aStops - bStops;
      }

      if (Math.abs(durationDiff) <= 15) {
        return priceDiff;
      }

      return durationDiff;
    };

    const sortByPrice = (
      a: FlightSearch.Itineraries,
      b: FlightSearch.Itineraries,
    ): number => {
      return (
        a.prices[0].totalPrice - b.prices[0].totalPrice ||
        getMinDuration(a) - getMinDuration(b)
      );
    };

    const sortByDate = (
      a: FlightSearch.Itineraries,
      b: FlightSearch.Itineraries,
      departureDate: boolean = true,
    ): number => {
      const getTime = (leg?: FlightSearch.Leg): number => {
        return departureDate
          ? leg?.dates.departureDate.getTime()
          : leg?.dates.arrivalDate.getTime() || 0;
      };
      return (
        getTime(a.outboundLeg) - getTime(b.outboundLeg) ||
        getTime(a.inboundLeg) - getTime(b.inboundLeg) ||
        a.prices[0].totalPrice - b.prices[0].totalPrice
      );
    };

    const sortByStopsDuration = (
      a: FlightSearch.Itineraries,
      b: FlightSearch.Itineraries,
    ): number => {
      const duration = (x: FlightSearch.Itineraries): number => {
        return (
          x.outboundLeg.duration.duration +
          (x.inboundLeg ? x.inboundLeg.duration.duration : 0)
        );
      };

      const stopsDuration = (x: FlightSearch.Itineraries): number => {
        return (
          x.outboundLeg.stopsDuration.duration +
          (x.inboundLeg ? x.inboundLeg.stopsDuration.duration : 0)
        );
      };

      return (
        stopsDuration(a) - stopsDuration(b) ||
        duration(a) - duration(b) ||
        a.prices[0].totalPrice - b.prices[0].totalPrice
      );
    };

    switch (state.filter.sortBy) {
      case FlightFilter.SortBy.Duration:
        return filtered.sort((a, b) => {
          return sortByDuration(a, b);
        });
      case FlightFilter.SortBy.Price:
        return filtered.sort((a, b) => {
          return sortByPrice(a, b);
        });
      case FlightFilter.SortBy.DepartureTime:
        return filtered.sort((a, b) => {
          return sortByDate(a, b);
        });
      case FlightFilter.SortBy.ArrivalTime:
        return filtered.sort((a, b) => {
          return sortByDate(a, b, false);
        });
      case FlightFilter.SortBy.StopsDuration:
        return filtered.sort((a, b) => {
          return sortByStopsDuration(a, b);
        });
      default:
        return filtered;
    }
  },

  offersByPage(state: State, getters): FlightSearch.Itineraries[] {
    const from = state.page * PAGE_LIMIT - PAGE_LIMIT;
    const to = state.page * PAGE_LIMIT;
    return getters.getOffers.slice(from, to);
  },

  pagesCount(state: State, getters): number {
    return Math.ceil(getters.getOffers.length / PAGE_LIMIT);
  },

  paginationBottomText(state: State, getters): string {
    const totalCount = getters.getOffers.length;
    const from = state.page === 1 ? 1 : PAGE_LIMIT * (state.page - 1) + 1;
    const to =
      state.page * PAGE_LIMIT < totalCount
        ? state.page * PAGE_LIMIT
        : totalCount;
    return i18n.global.t('searchpage.bottomText', {
      from,
      to,
      hotelsLength: totalCount,
    });
  },

  isBackPaginationEnable(state: State): boolean {
    return state.page > 1;
  },

  isNextPaginationEnable(state: State, getters): boolean {
    return state.page < getters.pagesCount;
  },

  errorMessage(state: State): string {
    return state.searchState.error && state.searchState.error.message
      ? state.searchState.error.message
      : '';
  },
};

const mutations = <MutationTree<State>>{
  updateSearchState(state: State, x: FlightSearch.IState) {
    state.searchState.error = x.error;
    state.searchState.state = x.state;
    if (x.searchFinishedTime) {
      state.searchState.searchFinishedTime = x.searchFinishedTime;
    }
  },

  setTripType(state: State, tripType: FlightCore.TripType) {
    state.searchCriteria.tripType = tripType;
  },

  setDepartDate(state: State, date: Date) {
    state.searchCriteria.departDate = date;
  },

  setReturnDate(state: State, date: Date) {
    state.searchCriteria.returnDate = date;
  },

  setCabinClass(state: State, cabinClass: FlightCore.CabinClass) {
    state.searchCriteria.cabinClass = cabinClass;
  },

  setFlightFromId(state: State, flightFromId: string) {
    state.searchCriteria.flightFromId = flightFromId;
  },

  setFlightToId(state: State, flightToId: string) {
    state.searchCriteria.flightToId = flightToId;
  },

  setAdults(state: State, passengers: number) {
    state.searchCriteria.adults = passengers;
  },

  addChild(state: State) {
    if (!Array.isArray(state.searchCriteria.childrenAges)) {
      state.searchCriteria.childrenAges = [];
    }

    state.searchCriteria.childrenAges.push(null);
  },

  removeChild(state: State) {
    const lastNullIndex = state.searchCriteria.childrenAges.lastIndexOf(null);

    if (lastNullIndex !== -1) {
      state.searchCriteria.childrenAges.splice(lastNullIndex, 1);
    } else {
      state.searchCriteria.childrenAges.pop();
    }
  },

  setChildAge(state: State, payload: { index: number; age: number }) {
    const { index, age } = payload;

    if (
      Array.isArray(state.searchCriteria.childrenAges) &&
      index >= 0 &&
      index < state.searchCriteria.childrenAges.length &&
      typeof age === 'number' &&
      age >= 0
    ) {
      state.searchCriteria.childrenAges[index] = age;
    }
  },

  setSkyscannerRedirectLink(state: State) {
    state.skyscannerRedirectLink =
      state.searchCriteria.generateSkyscannerRedirectLink();
  },

  setSearchCriteria(state: State, criteria: Flight.SearchCriteria) {
    state.searchCriteria = criteria;
  },

  showMoreFilters(state: State, show: boolean) {
    state.filterIsOpened = show;
  },

  setResults(state: State, results: FlightSearch.Itineraries[]) {
    state.results = results;
  },

  initFilter(state: State, filter: FlightFilter.Filter) {
    state.filter = filter;
  },

  setPriceRange(state: State, range: { left: number; right: number }) {
    if (state.results && state.filter) {
      state.filter.priceRange.left = range.left;
      state.filter.priceRange.right = range.right;
      state.page = 1;
    }
  },

  setSorting(state: State, index: number) {
    if (state.results && state.filter) {
      state.filter.sortBy = index;
      state.page = 1;
    }
  },

  setStops(state: State, index: number) {
    state.filter.stops.forEach(
      (it, i) => (it.selected = i !== index ? false : !it.selected),
    );
    state.page = 1;
  },

  applyFilter(state: State, filter: FlightFilter.Filter) {
    if (!filter) {
      return;
    }

    if (state.filter.priceRange) {
      state.filter.priceRange.right = filter.priceRange.right;
      state.filter.priceRange.left = filter.priceRange.left;
    }

    state.filter.carriers = filter.carriers;
    state.filter.sortBy = filter.sortBy;
    state.filter.stops = filter.stops;
    state.filter.airports = filter.airports;
    state.page = 1;
  },

  resetFilter(state: State) {
    state.filter.carriers.forEach(it => (it.selected = false));
    state.filter.priceRange.left = state.filter.priceRange.min;
    state.filter.priceRange.right = state.filter.priceRange.max;
    state.filter.stops.forEach(it => (it.selected = it.count === -1));
    state.page = 1;
  },

  incOrDecPage(state: State, direction: number) {
    if (state.results) {
      if (direction > 0) {
        state.page++;
      } else {
        if (state.page > 1) state.page--;
      }
    }
  },

  setPage(state: State, page: number) {
    state.page = page;
  },

  setNearbyDates(state: State, nearbyDatesWidget: FlightsWidgets.NearbyDates) {
    state.nearbyDatesWidget = nearbyDatesWidget;
  },

  setNearestPlace(state: State, destinations: FlightSearch.Destinations) {
    state.destinations = destinations;
  },

  setDestinationsLoading(state: State, loading: boolean) {
    state.destinationsLoading = loading;
  },
};

const actions = <ActionTree<State, any>>{
  async initSearchSession(store: ActionContext<State, any>) {
    if (pollingSubscription) {
      pollingSubscription.unsubscribe();
      pollingSubscription = null;
    }

    store.dispatch(
      'support/setRefreshDialogShow',
      { show: false, messgage: null },
      { root: true },
    );
    store.dispatch('support/stopCountdown', {}, { root: true });

    store.commit('updateSearchState', { state: FlightSearch.State.InProgress });
    store.commit('setResults', []);
    store.commit('initFilter', null);
    store.commit('setNearbyDates', null);
    store.commit('setPage', 1);

    const eventProps = await store.dispatch('composeEventProps');

    Events.Flights.Search.start(eventProps);

    FlightService.initSessionv3(store.state.searchCriteria).subscribe(
      async result => {
        await store.dispatch('liveSearchHandler', result);
        if (result.state !== SkyscannerResultsV3.ResultStatus.Complete) {
          store.dispatch('polling', result.sessionId);
        }
      },
      err => {
        const mapped = mapError(err);
        if (mapped.header === 'Bad Request') {
          mapped.message = i18n.global.t(
            'flights.search.badRequestError.subtitle',
          );
        }
        store.commit('updateSearchState', {
          state: FlightSearch.State.Failed,
          error: mapped,
        });
      },
    );
  },

  async polling(store: ActionContext<State, any>, sessionToken: string) {
    await store.dispatch('terminateSearch');
    pollingSubscription = FlightService.getLivePriceSessionV3(sessionToken)
      .pipe(timeoutWith(3 * 60 * 1000, throwError(new Error('timeout'))))
      .pipe(
        finalize(async () => {
          const eventProps = await store.dispatch('composeEventProps');
          Events.Flights.Search.finish(eventProps);
        }),
      )
      .subscribe(
        async result => {
          store.dispatch('liveSearchHandler', result);
        },
        err => {
          pollingSubscription?.unsubscribe();
          store.commit('updateSearchState', {
            state: FlightSearch.State.Failed,
            error: mapError(mapError(err)),
          });
        },
      );
  },

  async liveSearchHandler(
    store: ActionContext<State, any>,
    result: FlightSearch.LiveResults,
  ) {
    if (result.itineraries.length > 0) {
      const filter = FlightFilter.Filter.compose(
        result.itineraries,
        store.state.filter,
      );

      store.commit('initFilter', filter);
      store.commit('setResults', result.itineraries);
    }

    if (result.state === SkyscannerResultsV3.ResultStatus.Complete) {
      store.dispatch('initNearbyDatesWidget');
      pollingSubscription?.unsubscribe();

      store.commit('updateSearchState', {
        state: FlightSearch.State.Success,
        searchFinishedTime: Date.now(),
      });

      store.dispatch('logSearchHistory');
      await store.dispatch('support/initResfreshCountdown', { time: 15 * 60 * 1000 }, {
        root: true,
      });
    }
  },

  async composeEventProps(store: ActionContext<State, any>): Promise<Object> {
    const fromSuggest = (await store.rootGetters['suggest/getSuggest'](
      Suggest.Ids.FLIGHT_FROM,
    )) as Suggest.Suggest<Suggest.FlightSuggest>;

    const toSuggest = (await store.rootGetters['suggest/getSuggest'](
      Suggest.Ids.FLIGHT_TO,
    )) as Suggest.Suggest<Suggest.FlightSuggest>;

    const eventProps = {
      from: store.state.searchCriteria.flightFromId,
      from_city: fromSuggest.source.cityName,
      from_country: fromSuggest.source?.countryId,
      to: store.state.searchCriteria.flightToId,
      to_city: toSuggest.source.cityName,
      to_country: toSuggest.source.countryId,
      departure_date: store.state.searchCriteria.departDate
        ? format(store.state.searchCriteria.departDate, 'YYYY-MM-DD')
        : null,
      return_date: store.state.searchCriteria.returnDate
        ? format(store.state.searchCriteria.returnDate, 'YYYY-MM-DD')
        : null,
      adults: store.state.searchCriteria.adults,
      cabin_class: store.state.searchCriteria.cabinClass,
    };

    if (store.state.searchCriteria.childrenAges.length > 0) {
      eventProps['children_ages'] = store.state.searchCriteria.childrenAges;
      eventProps['children'] = store.state.searchCriteria.childrenAges.length;
    }

    return eventProps;
  },

  async logSearchHistory(store: ActionContext<State, any>) {
    const criteria = store.state.searchCriteria;
    const getDestinations = async (ids: Suggest.Ids): Promise<string> => {
      const suggest = await store.rootGetters['suggest/getSuggest'](ids);
      return suggest?.title;
    };
    const from = await getDestinations(Suggest.Ids.FLIGHT_FROM);
    const to = await getDestinations(Suggest.Ids.FLIGHT_TO);
    if (!from || !to) {
      return;
    }
    let price: string = undefined;
    try {
      price = store.state.results
        .find(it => it.prices.min(it => it.totalPrice))
        .prices.min(it => it.totalPrice)
        .totalPrice.formatCurrency({
          isCent: false,
          round: true,
        });
    } catch (e) {}

    const record: Flight.HistoryRecord = {
      from: from,
      to: to,
      criteria,
      price,
      updatedAt: new Date().getTime(),
    };
    store.dispatch('flightsHistory/write', record, { root: true });
  },

  terminateSearch(store: ActionContext<State, any>) {
    if (pollingSubscription) {
      pollingSubscription.unsubscribe();
      pollingSubscription = null;
    }
    if (nearbyDatesSubscription) {
      nearbyDatesSubscription.unsubscribe();
      nearbyDatesSubscription = null;
    }
  },

  onPageUnmount(store: ActionContext<State, any>) {
    store.dispatch('terminateSearch');
    store.commit('setResults', []);
    store.commit('initFilter', null);
  },

  initNearbyDatesWidget(store: ActionContext<State, any>) {
    if (nearbyDatesSubscription) {
      nearbyDatesSubscription.unsubscribe();
      nearbyDatesSubscription = null;
    }
    const getMinPrice = (): number | null => {
      try {
        const itineraries = store.state.results;

        return itineraries
          .filter(it => it.prices.length > 0)
          .min(it => it.prices[0].totalPrice).prices[0].totalPrice;
      } catch {
        return null;
      }
    };

    const minPrice = getMinPrice();

    nearbyDatesSubscription = FlightService.oneShotNearbyDates(
      store.state.searchCriteria,
      minPrice,
    ).subscribe(
      res => {
        store.commit('setNearbyDates', new FlightsWidgets.NearbyDates(res));
      },
      err => {
        console.log(err);
      },
    );
  },

  getNearestFlightsByGeo(store: ActionContext<State, any>) {
    store.commit('setDestinationsLoading', true);

    const buildDateAsString = (date: SkyscannerResultsV3.DateType): string => {
      const formatDate = (date: number): string => {
        return date < 10 ? `0${date}` : date.toString();
      };
      return `${formatDate(date.day)}${formatDate(date.month)}`;
    };

    const buildDate = (date: SkyscannerResultsV3.DateType): Date => {
      return new Date(date.year, date.month - 1, date.day, date.hour);
    };

    FlightService.getFlightsByGeo()
      .pipe(
        map(it => {
          const places: FlightSearch.Destination[] = it.destinations
            .filter(it => it.outboundLeg.destinationPlace.image)
            .map(it => {
              const departureDateAsString = buildDateAsString(
                it.outboundLeg.departureDateTime,
              );
              const link = `/flights/search/${it.outboundLeg.originPlace.iata}${departureDateAsString}${it.outboundLeg.destinationPlace.iata}`;
              const price = parsePrice(
                it.minPrice.amount,
                it.minPrice.unit,
              ).formatCurrency({
                isCent: false,
                round: true,
              });
              const image =
                it.outboundLeg.destinationPlace.image.resizeUrl[230];
              return {
                link,
                price,
                image,
                originIATA: it.outboundLeg.originPlace.iata,
                destinationIATA: it.outboundLeg.destinationPlace.iata,
                originName: it.outboundLeg.originPlace.name,
                destinationName: it.outboundLeg.destinationPlace.name,
                departDate: buildDate(it.outboundLeg.departureDateTime),
                title: `${it.outboundLeg.destinationPlace.city} from ${price}`,
              };
            });
          return {
            destinations: places,
            from: it.nearestPlace,
          };
        }),
      )
      .pipe(finalize(() => store.commit('setDestinationsLoading', false)))
      .subscribe(
        res => {
          store.commit('setNearestPlace', res);
        },
        err => {
          console.log(err);
        },
      );
  },
};

export const flight = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions,
};

export default {};
